import { useEffect } from 'react';

const SITE_URL = 'https://tuboscanaa.vercel.app';
const SITE_NAME = 'Tubos Canaã';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.webp`;

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export const SEOHead = ({
  title,
  description,
  path,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  noindex = false,
  jsonLd,
}: SEOHeadProps) => {
  const canonicalUrl = `${SITE_URL}${path}`;
  const fullTitle = path === '/' ? title : `${title} | ${SITE_NAME}`;

  useEffect(() => {
    // Title is cheap — set immediately
    document.title = fullTitle;

    // Batch remaining DOM writes after paint via idle callback
    const scheduleUpdate = (cb: () => void) => {
      if ('requestIdleCallback' in window) {
        return requestIdleCallback(cb, { timeout: 500 });
      }
      return setTimeout(cb, 0) as unknown as number;
    };

    const idleId = scheduleUpdate(() => {
      const setMeta = (attr: string, key: string, content: string) => {
        let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute(attr, key);
          document.head.appendChild(el);
        }
        el.setAttribute('content', content);
      };

      setMeta('name', 'description', description);
      if (noindex) {
        setMeta('name', 'robots', 'noindex, nofollow');
      } else {
        const robotsMeta = document.querySelector('meta[name="robots"]');
        if (robotsMeta) robotsMeta.remove();
      }

      setMeta('property', 'og:title', fullTitle);
      setMeta('property', 'og:description', description);
      setMeta('property', 'og:type', ogType);
      setMeta('property', 'og:url', canonicalUrl);
      setMeta('property', 'og:image', ogImage.startsWith('http') ? ogImage : `${SITE_URL}${ogImage}`);
      setMeta('property', 'og:site_name', SITE_NAME);
      setMeta('property', 'og:locale', 'pt_BR');

      setMeta('name', 'twitter:card', 'summary_large_image');
      setMeta('name', 'twitter:title', fullTitle);
      setMeta('name', 'twitter:description', description);
      setMeta('name', 'twitter:image', ogImage.startsWith('http') ? ogImage : `${SITE_URL}${ogImage}`);

      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalUrl);

      const scriptId = 'seo-json-ld';
      let scriptEl = document.getElementById(scriptId) as HTMLScriptElement | null;
      if (jsonLd) {
        if (!scriptEl) {
          scriptEl = document.createElement('script');
          scriptEl.id = scriptId;
          scriptEl.type = 'application/ld+json';
          document.head.appendChild(scriptEl);
        }
        const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
        scriptEl.textContent = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
      } else if (scriptEl) {
        scriptEl.remove();
      }
    });

    return () => {
      if ('requestIdleCallback' in window) {
        cancelIdleCallback(idleId as number);
      } else {
        clearTimeout(idleId as unknown as ReturnType<typeof setTimeout>);
      }
    };
  }, [fullTitle, description, canonicalUrl, ogImage, ogType, noindex, jsonLd]);

  return null;
};

// ─── Schema.org Helpers ───

export const SITE_URL_PUBLIC = SITE_URL;

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Manufacturer',
  name: 'Tubos Canaã',
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.svg`,
  description: 'Fabricante de tubos de PVC reciclados em Itaúna (MG) com 24 anos de história e foco em sustentabilidade.',
  foundingDate: '2000',
  telephone: '+553732429992',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Av. Manoel da Custódia, 682 — Bela Vista',
    addressLocality: 'Itaúna',
    addressRegion: 'MG',
    postalCode: '35680-403',
    addressCountry: 'BR',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: -20.0754,
    longitude: -44.8875,
  },
  sameAs: [],
  areaServed: {
    '@type': 'Country',
    name: 'Brasil',
  },
};

export const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: `${SITE_URL}${item.url}`,
  })),
});

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
};

export const productListSchema = (products: any[]) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: products.map((p, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    url: `${SITE_URL}/#${p.id}`,
    name: p.title,
    image: `${SITE_URL}${p.image}`,
  })),
});

export const productSchema = (product: { title: string; description: string; image: string; id: string }) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.title,
  description: product.description,
  image: `${SITE_URL}${product.image}`,
  sku: product.id,
  brand: {
    '@type': 'Brand',
    name: 'Tubos Canaã',
  },
  offers: {
    '@type': 'AggregateOffer',
    offerCount: '1',
    lowPrice: '0',
    priceCurrency: 'BRL',
    availability: 'https://schema.org/InStock',
    url: `${SITE_URL}/#${product.id}`,
  },
});
